import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as JSZip from 'jszip';
import { OrderService } from '../../services/order.service';
import OrderDetails, {
  TicketData,
} from '../../../../core/models/order.details';
import { OrderTicketData } from '../order-ticket/order-ticket.component';
import { OrderStatus } from 'src/app/core/enums/orderStatus.enum';

@Component({
  selector: 'order-detail-tickets',
  templateUrl: './order-detail-tickets.component.html',
  styleUrls: ['./order-detail-tickets.component.css'],
})
export class OrderDetailTicketsComponent implements OnInit {
  @Input() order: OrderDetails | null = null;
  ticketData: OrderTicketData | null = null;

  isLoading = false;
  isTicketPDFLink = false;

  selectedShareTickets: number[] = [];

  selectedDownloadTickets: number[] = [];

  selectedPrintTickets: number[] = [];

  private readonly ZIP_EXTRACT_THRESHOLD = 20;

  constructor(
    private route: ActivatedRoute,
    private orderService: OrderService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const tickets = this.route.snapshot.paramMap.get('ticketId');
    if (!tickets) return;

    this.isTicketPDFLink = true;
    const slug = this.route.snapshot.params['order_slug'];

    this.orderService.getDetail(slug).subscribe((resp) => {
      this.order = resp;
    });
  }

  get tickets(): any[] {
    return this.order ? this.order.tickets : [];
  }

  get orderCancelled(): boolean {
    return this.order?.status === OrderStatus.CANCELLED;
  }

  get canShare(): boolean {
    return !!navigator.canShare;
  }

  get canAddToWallet() {
    return this.order?.slug && this.order.status !== OrderStatus.CANCELLED;
  }

  getTicketLabel(ticket: any) {
    if (!ticket.isSeat) return '';
    let label = ticket.label;
    if (ticket.ticketType.block_id) label = label.replace(/\w+_/, ``);
    return label;
  }

  getGiftPicture(name: string) {
    if (name.toLowerCase() === 'brugal')
      return 'https://tix-shared.s3.us-east-1.amazonaws.com/Traguito400x400.png';
    return 'https://tix-shared.s3.us-east-1.amazonaws.com/Bocaito400x400.png';
  }

  getParsedTicketDataById(ticketId: number) {
    const ticket = this.tickets.find((t) => t.id === ticketId);
    return this.getParsedTicket(ticket);
  }

  private getParsedTicket(ticket: any) {
    return {
      eventDate: this.order?.event?.startAt ?? new Date(),
      eventName: this.order?.event?.name ?? '',
      buyerName: this.order?.buyerName ?? '',
      buyerEmail: this.order?.buyerEmail ?? '',
      eventPlace: this.order?.event?.place ?? '',
      orderId: this.order?.id ?? 0,
      printingDate: null,
      ticketId: ticket.id,
      ticketStatus: ticket.checkInDate === null ? 'Disponible' : 'Usado',
      ticketLabel: ticket.isSeat ? ticket.label.replace(/\w+_/, '') : undefined,
      ticketType: {
        data: { id: ticket.ticketType.id, attributes: ticket.ticketType },
      },
      qrSource: ticket.qrcode,
    };
  }

  private getSelectionList(type: 'share' | 'download' | 'print'): number[] {
    if (type === 'share') return this.selectedShareTickets;
    if (type === 'download') return this.selectedDownloadTickets;
    return this.selectedPrintTickets;
  }

  toggleSelectAll(type: 'share' | 'download' | 'print') {
    const availableTickets = this.tickets.filter((ticket) => {
      if (this.orderCancelled) return false;
      return !this.isTicketUsed(ticket);
    });

    const allSelected = this.isAllSelected(type);

    if (allSelected) {
      if (type === 'share') {
        this.selectedShareTickets = [];
      } else if (type === 'download') {
        this.selectedDownloadTickets = [];
      } else {
        this.selectedPrintTickets = [];
      }

      availableTickets.forEach((ticket) => {
        const el = document.getElementById(
          `modal-${type}-${ticket.id}`
        ) as HTMLInputElement;
        if (el) el.checked = false;
      });
    } else {
      availableTickets.forEach((ticket) => {
        const list = this.getSelectionList(type);
        if (!list.includes(ticket.id)) {
          const checkbox = document.getElementById(
            `modal-${type}-${ticket.id}`
          ) as HTMLInputElement;
          if (checkbox && !checkbox.disabled) {
            checkbox.checked = true;
            const changeEvent = new Event('change', { bubbles: true });
            checkbox.dispatchEvent(changeEvent);
          }
        }
      });
    }
  }

  isAllSelected(type: 'share' | 'download' | 'print'): boolean {
    const availableTickets = this.tickets.filter((ticket) => {
      if (this.orderCancelled) return false;
      return !this.isTicketUsed(ticket);
    });

    if (availableTickets.length === 0) return false;

    const list = this.getSelectionList(type);
    return availableTickets.every((t) => list.includes(t.id));
  }

  isTicketShared(ticketId: number) {
    const ticketIds = this.orderService.getTicketAsShared(this.order!.id);
    return ticketIds.includes(ticketId.toString());
  }

  onTicketCardClick(
    ticketId: number,
    type: 'share' | 'download' | 'print',
    event: MouseEvent
  ) {
    if ((event.target as HTMLElement).tagName === 'INPUT') return;
    const ticket = this.tickets.find((t) => t.id === ticketId);
    if (!ticket) return;
    if (this.orderCancelled || this.isTicketUsed(ticket)) return;

    const checkboxId = `modal-${type}-${ticketId}`;
    const checkbox = document.getElementById(checkboxId) as HTMLInputElement;

    if (checkbox && !checkbox.disabled) {
      checkbox.checked = !checkbox.checked;
      const changeEvent = new Event('change', { bubbles: true });
      checkbox.dispatchEvent(changeEvent);
    }
  }

  clearDownloadSelection() {
    this.selectedDownloadTickets = [];
    this.uncheckAll('modal-download');
  }

  private uncheckAll(prefixId: string) {
    for (const ticketId in this.tickets) {
      const ticket = this.tickets[ticketId];
      const el = document.getElementById(
        `${prefixId}-${ticket.id}`
      ) as HTMLInputElement;
      if (el) el.checked = false;
    }
  }

  onShareTicketChange(value: string, event: any) {
    const ticketId = +value;
    event.target.checked
      ? this.selectedShareTickets.push(ticketId)
      : (this.selectedShareTickets = this.selectedShareTickets.filter(
          (id) => id !== ticketId
        ));
  }

  async shareTickets() {
    if (!navigator.share || this.selectedShareTickets.length === 0 || !this.order) return;

    this.isLoading = true;
    try {
      const blob = await this.orderService.downloadTickets(
        this.order.slug,
        this.selectedShareTickets,
        true
      );

      const count = this.selectedShareTickets.length;
      let filesToShare: File[];
      if (count <= this.ZIP_EXTRACT_THRESHOLD) {
        filesToShare = await this.extractZipToFiles(blob);
      } else {
        filesToShare = [
          new File([blob], `Tickets-TIX-${this.order.id}.zip`, {
            type: 'application/zip',
          }),
        ];
      }

      if (navigator.canShare && !navigator.canShare({ files: filesToShare })) {
        console.warn('No se pueden compartir estos archivos según canShare');
        return;
      }

      try {
        await navigator.share({ files: filesToShare, title: 'Tickets' });
        for (const ticketId of this.selectedShareTickets) {
          this.orderService.saveTicketAsShared(this.order.id, ticketId);
        }
      } catch (shareError: any) {
        if (shareError.name !== 'AbortError') {
          console.error('Error al compartir:', shareError);
          throw shareError;
        }
      }
    } catch (error) {
      console.error('Error al compartir tickets:', error);
    } finally {
      this.isLoading = false;
      this.clearShareSelection();
    }
  }

  clearShareSelection() {
    this.selectedShareTickets = [];
  }

  onDownloadTicketChange(value: string, event: any) {
    const ticketId = +value;
    event.target.checked
      ? this.selectedDownloadTickets.push(ticketId)
      : (this.selectedDownloadTickets = this.selectedDownloadTickets.filter(
          (id) => id !== ticketId
        ));
  }

  async downloadSelectedTickets() {
    if (this.selectedDownloadTickets.length === 0 || !this.order) return;

    this.isLoading = true;
    try {
      const blob = await this.orderService.downloadTickets(
        this.order.slug,
        this.selectedDownloadTickets,
        true
      );

      const count = this.selectedDownloadTickets.length;
      if (count <= this.ZIP_EXTRACT_THRESHOLD) {
        const files = await this.extractZipToFiles(blob);
        files.forEach((file) => this.triggerBlobDownload(file, file.name));
      } else {
        this.triggerBlobDownload(
          blob,
          `Tickets-TIX-${this.order?.id}.zip`
        );
      }

      this.selectedDownloadTickets = [];
      this.closeModal('downloadModal');
    } catch (error) {
      console.error('Error downloading tickets:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private async extractZipToFiles(zipBlob: Blob): Promise<File[]> {
    const zip = await JSZip.loadAsync(zipBlob);
    const files: File[] = [];
    for (const name of Object.keys(zip.files)) {
      const entry = zip.files[name];
      if (entry.dir) continue;
      const content = await entry.async('blob');
      files.push(new File([content], name, { type: 'application/pdf' }));
    }
    return files;
  }

  private triggerBlobDownload(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  onPrintTicketChange(value: string, event: any) {
    const ticketId = +value;
    event.target.checked
      ? this.selectedPrintTickets.push(ticketId)
      : (this.selectedPrintTickets = this.selectedPrintTickets.filter(
          (id) => id !== ticketId
        ));
  }

  clearPrintSelection() {
    this.selectedPrintTickets = [];
    this.uncheckAll('modal-print');
  }

  async printSelectedTickets() {
    if (this.selectedPrintTickets.length === 0 || !this.order) return;

    this.isLoading = true;
    try {
      const file = await this.orderService.downloadTickets(
        this.order.slug,
        this.selectedPrintTickets,
        false
      );

      const url = window.URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Tickets-TIX-${this.order?.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      this.selectedPrintTickets = [];
      this.closeModal('printModal');
    } catch (error) {
      console.error('Error printing tickets:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private closeModal(modalId: string) {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      const bootstrap = (window as any).bootstrap;
      if (bootstrap?.Modal) {
        const instance =
          bootstrap.Modal.getInstance(modalElement) ||
          new bootstrap.Modal(modalElement);
        instance.hide();
      }
    }
  }

  trackByTicketId(index: number, ticket: any): number {
    return ticket.id;
  }

  isTicketUsed(ticket: any): boolean {
    return ticket.status === 'used';
  }

  isTicketAvailable(ticket: any): boolean {
    return ticket.status === 'available';
  }

  getTicketStatusBadgeStyle(ticket: any): string {
    if (this.orderCancelled) {
      return 'background-color: #ffe2e2; color: #c10007;';
    }
    if (this.isTicketUsed(ticket)) {
      return 'background-color: #ffe2e2; color: #c10007;';
    }
    return 'background-color: #dcfce7; color: #008236;';
  }

  getTicketStatusText(ticket: any): string {
    if (this.orderCancelled) {
      return 'Cancelado';
    }
    if (this.isTicketUsed(ticket)) {
      return 'Usado';
    }
    return 'Disponible';
  }

  getTicketStatusBadgeStyleWithShared(ticket: any): string {
    if (this.orderCancelled) {
      return 'background-color: #ffe2e2; color: #c10007;';
    }
    if (this.isTicketShared(ticket.id)) {
      return 'background-color: #fef9c2; color: #a65f00;';
    }
    if (this.isTicketUsed(ticket)) {
      return 'background-color: #ffe2e2; color: #c10007;';
    }
    return 'background-color: #dcfce7; color: #008236;';
  }

  getTicketStatusTextWithShared(ticket: any): string {
    if (this.orderCancelled) {
      return 'Cancelado';
    }
    if (this.isTicketShared(ticket.id)) {
      return 'Compartido';
    }
    if (this.isTicketUsed(ticket)) {
      return 'Usado';
    }
    return 'Disponible';
  }
}
